import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { FormlyMaterialModule } from '@ngx-formly/material';
import { FormlyMatDatepickerModule } from '@ngx-formly/material/datepicker';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginatorModule, MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { Subject, takeUntil } from 'rxjs';
import dayjs from 'dayjs';
import {
  PaymentProcessingService,
  SanityCheckInvalidPayment,
  SanityCheckMultiLoanCustomer,
} from '@lmc-app/core/services/payment-processing.service';
import { FormatUtil } from '@lmc-app/shared/utils/format.util';
import { CUSTOM_DATE_FORMAT } from '@lmc-app/shared/constants/common.constant';

interface SanityCheckFilterModel {
  dateFrom: Date | null;
  dateTo: Date | null;
}

@Component({
  selector: 'app-sanity-check-dialog',
  templateUrl: './sanity-check-dialog.component.html',
  styleUrls: ['./sanity-check-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormlyModule,
    FormlyMaterialModule,
    FormlyMatDatepickerModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatTableModule,
    MatTabsModule,
    MatTooltipModule,
  ],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMAT },
  ],
})
export class SanityCheckDialogComponent implements OnInit, OnDestroy {
  // Two tables, so the paginators are addressed by template reference rather than by type,
  // and bound through setters rather than read after a run: Material instantiates a tab's
  // content only when that tab is opened, so the paginator of the tab the operator has not
  // looked at yet does not exist when the results arrive. The setter binds each one at the
  // moment it appears — first render for the open tab, first visit for the other.
  @ViewChild('invalidPaginator') set invalidPaginator(pag: MatPaginator | undefined) {
    this.dataSource.paginator = pag ?? null;
  }

  @ViewChild('multiLoanPaginator') set multiLoanPaginator(pag: MatPaginator | undefined) {
    this.multiLoanDataSource.paginator = pag ?? null;
  }

  readonly FormatUtil = FormatUtil;

  readonly displayedColumns: string[] = [
    'expand',
    'loanNumber',
    'customerName',
    'dueDate',
    'maturityDate',
    'scheduledAmount',
    'expectedAmount',
    'difference',
  ];

  /** Column set of the nested breakdown table rendered in an expanded row. */
  readonly componentColumns: string[] = [
    'paymentNumber',
    'paymentType',
    'paymentDescription',
    'amount',
    'paid',
    'remaining',
  ];

  /** Column set of the multi-loan customers table. */
  readonly customerColumns: string[] = [
    'expandCustomer',
    'customerName',
    'cifnos',
    'linkedBy',
    'loanCount',
    'loanNumbers',
  ];

  /** Column set of the nested loan table rendered in an expanded customer row. */
  readonly customerLoanColumns: string[] = [
    'loanNumber',
    'acctrefno',
    'cifno',
    'dueDate',
    'paymentAmount',
  ];

  /** paymentReferenceNo of every expanded row. */
  private readonly expanded = new Set<string>();

  /** groupId of every expanded customer row (a customer can span several cifnos). */
  private readonly expandedCustomers = new Set<string>();

  /** Earliest selectable due date. The dialog is rebuilt per open, so this stays current. */
  readonly minDate = dayjs().startOf('day').toDate();

  readonly form = new FormGroup({});
  model: SanityCheckFilterModel = { dateFrom: null, dateTo: null };
  fields: FormlyFieldConfig[] = [];

  dataSource = new MatTableDataSource<SanityCheckInvalidPayment>([]);
  pageSize = 10;

  multiLoanDataSource = new MatTableDataSource<SanityCheckMultiLoanCustomer>([]);
  multiLoanPageSize = 10;

  isRunning = false;
  /** Set once a check has completed, so the empty state only shows after a run. */
  hasRun = false;
  /** True when the backend hit its result cap and left rows out. */
  truncated = false;
  /** True when the backend hit its result cap and left customers out. */
  multiLoanTruncated = false;
  errorMessage: string | null = null;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly dialogRef: MatDialogRef<SanityCheckDialogComponent>,
    private readonly paymentProcessingService: PaymentProcessingService,
  ) {}

  ngOnInit(): void {
    this.fields = this.buildFields();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Formly config for the From/To range.
   *
   * The calendar toggle is left in its default `suffix` slot. The filter row on the list
   * page moves it to the prefix so a clear (x) button can own the suffix, but both dates
   * here are required — there is nothing to clear — so the toggle keeps the default
   * position used by every other datepicker in the app.
   *
   * Both fields are floored at today, so the check only ever looks at upcoming due dates.
   */
  private buildFields(): FormlyFieldConfig[] {
    const dateField = (key: string, label: string): FormlyFieldConfig => ({
      key,
      type: 'datepicker',
      props: {
        label,
        placeholder: 'Select date',
        appearance: 'outline',
        required: true,
        datepickerOptions: { min: this.minDate },
      },
    });

    return [
      {
        fieldGroupClassName: 'date-range-grid',
        fieldGroup: [dateField('dateFrom', 'From'), dateField('dateTo', 'To')],
      },
    ];
  }

  /**
   * Both dates are required and the range must not be inverted. `form.valid` also covers
   * the datepicker's own errors — a date typed in by hand rather than picked can still be
   * before `minDate` (matDatepickerMin) or unparseable (matDatepickerParse).
   */
  get isRangeValid(): boolean {
    const { dateFrom, dateTo } = this.model;
    if (!dateFrom || !dateTo) return false;
    if (this.form.invalid) return false;
    return !dayjs(dateFrom).startOf('day').isAfter(dayjs(dateTo).startOf('day'));
  }

  /**
   * Whether the tabs have something to report: a run that came back. A failed run leaves the
   * tables empty but is not a result, so its tabs go back to the placeholder rather than
   * claiming the check found nothing — the error notice above says what happened.
   */
  get hasResults(): boolean {
    return this.hasRun && !this.errorMessage;
  }

  get invalidCount(): number {
    return this.dataSource.data.length;
  }

  get multiLoanCount(): number {
    return this.multiLoanDataSource.data.length;
  }

  isCustomerExpanded(row: SanityCheckMultiLoanCustomer): boolean {
    return this.expandedCustomers.has(row.groupId);
  }

  toggleCustomerExpanded(row: SanityCheckMultiLoanCustomer): void {
    if (!this.expandedCustomers.delete(row.groupId)) {
      this.expandedCustomers.add(row.groupId);
    }
  }

  /**
   * The CIF records behind a customer. Two or more means the same person was set up twice in
   * NLS — the loans were tied together by name and bank account instead.
   */
  cifnos(row: SanityCheckMultiLoanCustomer): string {
    return row.cifnos.join(', ');
  }

  /** Loan numbers for the collapsed row, the ones found in the date range first. */
  loanNumbers(row: SanityCheckMultiLoanCustomer): string {
    return row.loans.map((loan) => loan.loanNumber).join(', ');
  }

  /**
   * How far the scheduled payment sits from what the loan should be charged. Derived here
   * rather than on the backend — both amounts are already on the row, so this is
   * presentation only.
   */
  difference(row: SanityCheckInvalidPayment): number {
    return Number((row.scheduledAmount - row.expectedAmount).toFixed(2));
  }

  /** Spells out how expectedAmount was built, for the column's tooltip. */
  expectedBreakdown(row: SanityCheckInvalidPayment): string {
    const amortized = `amortized ${FormatUtil.formatCurrency(row.amortizedAmount)}`;
    if (row.recurringAmount === 0) return amortized;
    return `${amortized} + insurance/tax ${FormatUtil.formatCurrency(row.recurringAmount)}`;
  }

  isExpanded(row: SanityCheckInvalidPayment): boolean {
    return this.expanded.has(row.paymentReferenceNo);
  }

  toggleExpanded(row: SanityCheckInvalidPayment): void {
    if (!this.expanded.delete(row.paymentReferenceNo)) {
      this.expanded.add(row.paymentReferenceNo);
    }
  }

  /** Sum of the payment's component rows. */
  componentsTotal(row: SanityCheckInvalidPayment): number {
    return Number(row.components.reduce((sum, c) => sum + c.amount, 0).toFixed(2));
  }

  /**
   * True when the breakdown does not add up to the charged (ZZ) amount. Rare but real — the
   * NLS total row can disagree with its own components — so the expanded view calls it out
   * instead of showing a subtotal that silently contradicts the row above.
   */
  hasComponentMismatch(row: SanityCheckInvalidPayment): boolean {
    return Math.round(this.componentsTotal(row) * 100) !== Math.round(row.scheduledAmount * 100);
  }

  onExecute(): void {
    if (!this.isRangeValid || this.isRunning) {
      this.form.markAllAsTouched();
      return;
    }

    // A new run replaces the rows, so stale references must not stay expanded.
    this.expanded.clear();
    this.expandedCustomers.clear();

    this.isRunning = true;
    this.errorMessage = null;

    const dateFrom = dayjs(this.model.dateFrom!).format('YYYY-MM-DD');
    const dateTo = dayjs(this.model.dateTo!).format('YYYY-MM-DD');

    this.paymentProcessingService
      .runSanityCheck(dateFrom, dateTo)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.dataSource.data = response.results ?? [];
          this.truncated = response.truncated ?? false;
          this.multiLoanDataSource.data = response.multiLoanCustomers ?? [];
          this.multiLoanTruncated = response.multiLoanTruncated ?? false;
          this.hasRun = true;
          this.isRunning = false;
          // A re-run replaces both result sets, so both tables go back to their first page.
          // Addressed through the data source, not the view: the paginator of an unopened
          // tab does not exist yet, and one created later starts on page 1 anyway.
          this.dataSource.paginator?.firstPage();
          this.multiLoanDataSource.paginator?.firstPage();
        },
        error: (err) => {
          this.dataSource.data = [];
          this.truncated = false;
          this.multiLoanDataSource.data = [];
          this.multiLoanTruncated = false;
          this.hasRun = true;
          this.isRunning = false;
          this.errorMessage =
            err?.error?.message ?? err?.message ?? 'Failed to run the sanity check. Please try again.';
        },
      });
  }

  onPageChange(event: PageEvent): void {
    this.pageSize = event.pageSize;
  }

  onMultiLoanPageChange(event: PageEvent): void {
    this.multiLoanPageSize = event.pageSize;
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}